import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Card,
  CardActions,
  CardMedia,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MobileTimePicker } from '@mui/x-date-pickers';
import { AccessTime } from '@mui/icons-material';

import { postUploadFile } from './etc';
import Form from 'components/Form';
import MuiDialog from './components/MuiDialog';
import Uploader from './components/Uploader';
import useNotification from 'hooks/useNotification';

import { IRadiology } from '../PatientList/api/type';

interface Props {
  isUpdate: boolean;
  defaultValue: Partial<IRadiology>;
  onClose: () => void;
  onSave: (data: IRadiology) => void;
  onSearchNo: (no: number) => boolean;
}

const RadiologyDialog = (props: Props) => {
  const { isUpdate, defaultValue, onClose, onSave, onSearchNo } = props;

  const { onSuccess, onRequired, onResultCode, onFail } = useNotification();
  const { handleSubmit, register, getValues, setValue } = useForm({
    defaultValues: defaultValue,
  });

  const [isSearched, setIsSearched] = useState(isUpdate);
  const [radiologyTime, setRadiologyTime] = useState<Date | null>(
    new Date(defaultValue.radiologyTime!)
  );

  const [files, setFiles] = useState<string[]>(
    defaultValue.radiologyImages ?? []
  );
  const onUploadFile = (file: File) => {
    if (files.length === 5) return onRequired('REQUIRED.RADIOLOGY.MAX.FILE');

    postUploadFile({ file })
      .then(({ data: { rc, bucket_url } }) => {
        if (rc !== 1) return onResultCode(rc);
        setFiles([...files, bucket_url]);
        onSuccess('파일업로드 성공하였습니다.');
      })
      .catch(e => onFail('파일업로드 실패하였습니다', e));
  };

  const DeleteFile = ({ index }: { index: number }) => {
    const onDelete = () =>
      setFiles(files.filter((_, fIndex) => fIndex !== index));
    return (
      <Button fullWidth size="small" color="error" onClick={onDelete}>
        영상파일{index + 1} 삭제하기
      </Button>
    );
  };

  const onSubmit = (data: any) => {
    if (!isUpdate && onSearchNo(Number(data.ptRadiologyNo)))
      return onRequired('REQUIRED.UNIQUE.NO');
    if (!isSearched) return onRequired('REQUIRED.FEE.ID');

    onSuccess('저장되었습니다');
    onSave({ ...data, radiologyImages: files, radiologyTime });
    onClose();
  };

  const onChangeFeeId = (e: any) => {
    const { value } = e.target;
    setValue('feeId', value);
    setValue('feeKor', '');
    setValue('feeEng', '');
    setIsSearched(false);
  };

  return (
    <MuiDialog.SaveForm
      isOpen
      maxWidth="md"
      title="영상검사결과 데이터"
      onClose={onClose}
      onSubmit={e => {
        e.stopPropagation();
        handleSubmit(onSubmit)(e);
      }}
    >
      <Grid container gap={2} sx={{ my: 2 }}>
        <Form.Item required label={'검사번호'}>
          <TextField
            required
            size="small"
            type="number"
            {...register('ptRadiologyNo')}
            InputProps={{ readOnly: isUpdate }}
          />
        </Form.Item>
        <Form.Item required label={'검사시간'}>
          <MobileTimePicker
            value={radiologyTime}
            onChange={setRadiologyTime}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                fullWidth={false}
                InputProps={{ endAdornment: <AccessTime /> }}
              />
            )}
          />
        </Form.Item>
        <Grid item xs={12} />

        <Form.Item required label={'검사코드'}>
          <TextField required size="small" {...register('feeId')} />
        </Form.Item>

        <Form.Item label={'검사명(한)'}>
          <TextField
            size="small"
            {...register('feeKor')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Form.Item label={'검사명(영)'}>
          <TextField
            size="small"
            {...register('feeEng')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Grid item xs={12}>
          <Form.Item required fullWidth label={'판독결과'}>
            <TextField
              required
              fullWidth
              multiline
              minRows={5}
              size="small"
              {...register('radiologyResult')}
            />
          </Form.Item>
        </Grid>

        <Grid item xs={12}>
          <Form.Item required fullWidth label={'판독소견'}>
            <TextField
              required
              fullWidth
              multiline
              minRows={5}
              size="small"
              {...register('radiologyNote')}
            />
          </Form.Item>
        </Grid>

        <Grid item xs={12}>
          <Grid
            container
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Stack spacing={1}>
              <Typography variant="body2">
                업로드할 파일을 선택해주세요
              </Typography>
              <Typography variant="caption" color="gray">
                5MB 이하 최대 5개, '.png, .jpg, .jpeg' 형식의 파일만 가능합니다.
              </Typography>
            </Stack>
            <Uploader
              maxSize={5}
              buttonLabel="파일 업로드"
              id="radiology-uploader"
              accept=".png, .jpg, .jpeg"
              onChange={fileList => onUploadFile(fileList[0])}
            />
          </Grid>
        </Grid>

        {files.map((file, index) => (
          <Grid item xs={2.4} key={file}>
            <Form.Item label={`영상파일${index + 1}`}>
              <Card sx={{ maxWidth: '100%' }} variant="outlined">
                <CardMedia component="img" image={file} />
                <CardActions>
                  <DeleteFile index={index} />
                </CardActions>
              </Card>
            </Form.Item>
          </Grid>
        ))}
      </Grid>
    </MuiDialog.SaveForm>
  );
};

export default RadiologyDialog;
